import { z } from 'zod';
import { DisplayName, SessionKind, Ulid, Username } from './common.js';
import { SelfTier, Tier } from './features.js';
/**
 * Authentication contract (plan §5).
 *
 * Access tokens are short-lived HMAC JWTs; refresh tokens are opaque random bytes stored
 * only as a hash and rotated on every use. The device-code grant follows RFC 8628 shapes
 * so a Board — and an editor plugin, which has the same "no keyboard worth typing on"
 * problem — can sign in by showing a code instead of a password field.
 */
export const Email = z
    .string()
    .trim()
    .toLowerCase()
    .pipe(z.email('Must be a valid email address.'))
    .refine((s) => s.length <= 254, 'Email address is too long.');
/**
 * 12 characters minimum with no composition rules: length is the only password requirement
 * that reliably helps, and the rest just pushes people towards `Passw0rd!`.
 */
export const Password = z
    .string()
    .min(12, 'Must be at least 12 characters.')
    .max(1024, 'Must be at most 1024 characters.');
export const SignupRequest = z.object({
    email: Email,
    password: Password,
    /** How everybody else will know this account. Required, unique, and the only name a stranger
     *  ever sees. */
    username: Username,
    /**
     * The person's real name — optional, and asked for only because a team's own roster reads
     * better with it. It is never served to anybody outside a team they are in, so leaving it out
     * costs nothing but a slightly emptier member list.
     *
     * The dashboard no longer sends it, nor `teamName`: both moved to step 1 of the creator
     * onboarding (`creator-onboarding.ts`), because a tester or a player signing up has no use for
     * either. Still accepted, for a dashboard tab opened before that change and for the tests.
     */
    displayName: DisplayName.optional(),
    /** Optional: create a first team in the same call. See `displayName` for who still sends it. */
    teamName: DisplayName.optional(),
    /**
     * The signup form's "Email me about new games" box (`docs/new-game-emails.md`). Absent is no —
     * the box starts unticked, because a consent somebody had to untick is not one.
     */
    newGameEmails: z.boolean().optional(),
});
/**
 * `PATCH /v1/me` — the things about an account its owner can change.
 *
 * All optional and all meaningful when absent: an empty body is a no-op, not a clear. Clearing
 * the real name is `displayName: null`, which is the only way to take it back after giving it.
 */
export const UpdateMeRequest = z.object({
    username: Username.optional(),
    displayName: DisplayName.nullable().optional(),
    /**
     * Email about new games, on or off (`docs/new-game-emails.md`). Either answer also settles the
     * question a Google or Discord signup is asked once, so it is not asked again.
     */
    newGameEmails: z.boolean().optional(),
    /**
     * Off, no report typed on this account's Boards carries its name, and its desktop apps are never
     * rung for feedback (`docs/feedback-plan.md` §6.3).
     */
    namedFromBoards: z.boolean().optional(),
    /**
     * Join the beta (`beta`) or leave it (`free`) — `docs/plans-and-features.md` §2. Somebody in the
     * alpha leaves it by choosing either, and only an operator puts them back; nobody puts themselves
     * in the alpha, which is why `alpha` is not a value here.
     */
    tier: SelfTier.optional(),
});
export const LoginRequest = z.object({
    email: Email,
    password: z.string().min(1),
    /** Shown in the revocable sessions list, e.g. "MacBook · Chrome" or "Unity Editor". */
    label: z.string().trim().max(120).optional(),
    kind: SessionKind.default('web'),
});
export const RefreshRequest = z.object({
    refreshToken: z.string().min(1),
});
/**
 * The identity providers an account can be signed in with.
 *
 * Two entries, and the second one cost what the enum was written to make it cost: a value here,
 * a row in `PROVIDERS` (`apps/api/src/lib/oauth.ts`) and two credentials. Not a second copy of
 * `routes/oauth.ts`, and not a second kind of account — see that file.
 */
export const OAuthProvider = z.enum(['discord', 'google']);
/**
 * `POST /v1/auth/:provider/exchange` — the second half of a provider sign-in.
 *
 * The provider redirects a *browser* back to the API, and what the dashboard needs is a token
 * pair. So the callback hands the browser a single-use code that names an account and authorizes
 * nothing, and this is where it is spent: the session is created here, by a script, exactly as a
 * password sign-in creates one — same `label` and `kind`, same rotation, same revocation.
 */
export const OAuthExchangeRequest = z.object({
    code: z.string().min(1),
    /**
     * The secret whose SHA-256 the dashboard sent to `/start` as `challenge`. Only the browser that
     * started the sign-in has it, so only that browser can redeem the code.
     */
    verifier: z.string().min(43).max(128),
    /** Shown in the revocable sessions list, e.g. "Dashboard". */
    label: z.string().trim().max(120).optional(),
    kind: SessionKind.default('web'),
});
export const LogoutRequest = z.object({
    refreshToken: z.string().min(1),
});
/**
 * How a **browser** asks to keep its session in a cookie rather than in the page
 * (`docs/library-first-plan.md` §3).
 *
 * Sign-in, signup, refresh, sign-out and the OAuth exchange each answer two kinds of caller with
 * one handler. The CLI, the editor plugins, the desktop app and the admin console send and
 * receive the refresh token in the JSON body, as they always have. A page on one of the two web
 * origins sends this header instead, and then the refresh token travels only as an `HttpOnly`
 * cookie on the API's own host: the response body carries everything but it, and a refresh or a
 * sign-out carries no token in its body at all.
 *
 * The header is not what makes that safe — any page can send a header. It is what makes the
 * request one a browser must *ask permission for*: a custom header forces a CORS preflight, and
 * the API answers that preflight for two origins and no others.
 */
export const SESSION_TRANSPORT_HEADER = 'x-boardflight-session';
export const SESSION_TRANSPORT_COOKIE = 'cookie';
/**
 * The signed-in account, as it is returned to *itself*. This is the one place `email` and
 * `displayName` are unconditional, because the reader is the subject — `UserIdentity` is the shape
 * every other account appears in.
 */
export const PublicUser = z.object({
    id: Ulid,
    email: z.string(),
    username: z.string(),
    /** Null when the account never gave a real name, which is now the default. */
    displayName: z.string().nullable(),
    createdAt: z.number().int(),
    /**
     * Whether the account has proven it receives mail at `email` — by the link signup sends, or by
     * signing in with a provider that vouches for the address. Joining a team by invite, receiving
     * a game by email and starting a playground all wait for it.
     */
    emailVerified: z.boolean(),
    /**
     * Present, and `true`, only for a project admin (`users.is_admin`, migration 0027).
     *
     * `true | undefined` rather than a boolean, because the key must be **absent** for everybody
     * else: the admin console is invisible to accounts that cannot reach it, and an `admin: false`
     * in every sign-in response announces that such a thing exists. Same shape, and the same
     * reason, as `Team.features` shipping only the keys that are on (docs/admin-plan.md §D4).
     *
     * This is a project admin, not `team_members.role = 'admin'`, which administers one team and
     * reaches none of it.
     */
    admin: z.literal(true).optional(),
    /**
     * Where the admin console lives, beside `admin` and only in an admin's own `GET /v1/me`. The
     * console's host is deliberately unguessable, so it is told to the people who can open it and
     * compiled into nothing anybody else downloads.
     */
    adminUrl: z.string().optional(),
    /**
     * Present, and `true`, only for an account on the analytics exclusion list (docs/admin-plan.md
     * §3.3) — and only in its own token pair and `GET /v1/me`. It is what tells the account's own
     * browser to send no product analytics; the server already leaves it out of everything else.
     * Absent rather than false for the reason `admin` is: nobody else needs to learn the list exists.
     */
    analyticsExcluded: z.literal(true).optional(),
    /**
     * Whether the account wants email about new games (`docs/new-game-emails.md`). In `GET` and
     * `PATCH /v1/me` only — never in a token pair, because a Board or an editor signing in has no use
     * for a marketing preference — so absent means *not said here*, not *off*.
     */
    newGameEmails: z.boolean().optional(),
    /**
     * Whether a report typed on a Board this account paired may carry its name
     * (`docs/feedback-plan.md` §6.3). In `GET` and `PATCH /v1/me` only, like `newGameEmails`.
     */
    namedFromBoards: z.boolean().optional(),
    /**
     * How early this account gets new features: `free`, in the `beta` it joined itself, or in the
     * `alpha` an operator put it in (`docs/plans-and-features.md` §2). In `GET` and `PATCH /v1/me`
     * only, like `newGameEmails`. Not a plan: what a team pays for is never in any response.
     */
    tier: Tier.optional(),
});
export const TokenPair = z.object({
    accessToken: z.string(),
    /** Epoch milliseconds at which `accessToken` stops being accepted. */
    accessTokenExpiresAt: z.number().int(),
    refreshToken: z.string(),
    refreshTokenExpiresAt: z.number().int(),
    sessionId: Ulid,
    user: PublicUser,
});
/**
 * A token pair as a page receives it over the cookie transport (`SESSION_TRANSPORT_HEADER`): all
 * of `TokenPair` but the refresh token, which a page is never shown.
 */
export const WebSession = TokenPair.omit({ refreshToken: true });
export const SessionSummary = z.object({
    id: Ulid,
    kind: SessionKind,
    label: z.string().nullable(),
    createdAt: z.number().int(),
    lastSeenAt: z.number().int().nullable(),
    expiresAt: z.number().int(),
    /** True for the session that made this request — the UI must not offer "revoke" blindly. */
    current: z.boolean(),
});
/* ------------------------------------------------------------------ device-code grant */
export const DeviceStartRequest = z.object({
    /** Free-form, shown on the approval page so the user knows what they are authorising. */
    clientName: z.string().trim().min(1).max(120),
    /**
     * The desktop app (`device`) or an editor (`plugin`). The desktop app signs in this way because
     * Google refuses OAuth inside an embedded webview (`docs/off-board-plan.md` P1). Never `web`: a
     * dashboard signs in with a password or a provider, and a device code that minted a dashboard
     * session would let anyone who can start one ask for one.
     */
    kind: z.enum(['device', 'plugin']).default('device'),
});
export const DeviceStartResponse = z.object({
    deviceCode: z.string(),
    /** Human-typable, grouped for reading off a Board's screen: `WXYZ-4821`. */
    userCode: z.string(),
    verificationUri: z.string(),
    verificationUriComplete: z.string(),
    expiresIn: z.number().int(),
    /** Seconds the client must wait between polls. */
    interval: z.number().int(),
});
export const DevicePollRequest = z.object({
    deviceCode: z.string().min(1),
});
/**
 * RFC 8628 error codes. `authorization_pending` and `slow_down` are 400s that the client
 * treats as "keep polling"; the rest are terminal.
 */
export const DevicePollError = z.enum([
    'authorization_pending',
    'slow_down',
    'expired_token',
    'access_denied',
]);
export const DeviceApproveRequest = z.object({
    userCode: z.string().min(1),
    /** `false` lets the user reject a code they did not initiate. */
    approve: z.boolean().default(true),
});
export const DeviceCodeInfo = z.object({
    userCode: z.string(),
    clientName: z.string(),
    kind: SessionKind,
    expiresAt: z.number().int(),
});
//# sourceMappingURL=auth.js.map